// utils/bind-external-toggle.util.ts
import { AbstractControl, FormControl } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DestroyRef, inject } from "@angular/core";
import { startWith } from "rxjs";

type Target =
  | AbstractControl
  | { ctl: AbstractControl; map?: (baseValue: any) => any };

/**
 * Lie un FormControl<boolean> externe à un ou plusieurs contrôles du form.
 * - checked = true  -> disable + setValue( compute() | map(compute()) )
 * - checked = false -> enable  + setValue( reset     | map(reset) )
 */
export function bindExternalToggleDisablesControl(
  toggleCtl: FormControl<boolean>,
  targets: Target[] | Target,
  compute: () => any, // valeur "auto" quand activé (ex: this.blocktime)
  reset: any, // valeur quand désactivé (ex: 0)
  destroyRef: DestroyRef = inject(DestroyRef),
  opts?: {
    emitOnSet?: boolean; // def: true
    syncOnInit?: boolean; // def: true
  }
) {
  const tgs = Array.isArray(targets) ? targets : [targets];
  const emitOnSet = opts?.emitOnSet ?? true;
  const syncOnInit = opts?.syncOnInit ?? true;

  toggleCtl.valueChanges
    .pipe(
      startWith(syncOnInit ? toggleCtl.value : undefined),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe((checked) => {
      if (checked === undefined) return;

      const base = checked ? compute() : reset;

      for (const t of tgs) {
        const ctl = (t as any).ctl
          ? ((t as any).ctl as AbstractControl)
          : (t as AbstractControl);
        const map = (t as any).map as ((v: any) => any) | undefined;

        // désactive/active
        checked
          ? ctl.disable({ emitEvent: false })
          : ctl.enable({ emitEvent: false });

        // applique la valeur (avec mapping éventuel)
        const v = map ? map(base) : base;
        ctl.setValue(v, { emitEvent: emitOnSet });
      }
    });
}
